import React from 'react'
import { useTranslation } from 'react-i18next'
import { BadgeCheck, Download, FileSpreadsheet, FileUp, FolderPlus, Lock, LockOpen, MoreVertical, Settings } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import type { BudgetAffordances } from '../lib/permissions'

interface Props {
  affordances: BudgetAffordances
  lockedAt: string | null
  locale: string
  /** Name of the level-1 group the grid is filtered to; the export follows the filter. */
  rootName: string | null
  onAddLevelOne: () => void
  onImport?: () => void
  onExport: (format: 'xlsx' | 'csv') => void
  onLock: () => void
  onUnlock: () => void
  onSettings: () => void
}

/**
 * Lock state and the everyday actions stay visible (export, add); the occasional ones (import, lock / unlock,
 * settings) sit in the overflow menu, as on the schedule detail. The empty state keeps its own import button.
 */
const BudgetActions: React.FC<Props> = ({ affordances: af, lockedAt, locale, rootName, onAddLevelOne, onImport, onExport, onLock, onUnlock, onSettings }) => {
  const { t } = useTranslation()
  const canImport = af.importBudget && !!onImport
  const hasLockItem = af.lock || af.unlock
  return (
    <>
      {lockedAt && (
        <Badge variant="success" className="gap-1" title={t('CostControl_locked_since', { date: new Date(lockedAt).toLocaleDateString(locale) })}>
          <BadgeCheck className="h-3.5 w-3.5" />
          {t('CostControl_locked_badge')}
        </Badge>
      )}
      {af.exportBudget && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8">
              <Download className="h-4 w-4" />
              {t('CostControl_export')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel className="font-normal text-muted-foreground">
              {rootName ? t('CostControl_export_scope_level_one', { name: rootName }) : t('CostControl_export_scope_all')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onExport('xlsx')}><FileSpreadsheet className="h-4 w-4 mr-2" />{t('CostControl_export_xlsx')}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport('csv')}><FileSpreadsheet className="h-4 w-4 mr-2" />{t('CostControl_export_csv')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {(canImport || hasLockItem || af.openSettings) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" aria-label={t('Common_more')} title={t('Common_more')}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canImport && <DropdownMenuItem onClick={onImport}><FileUp className="h-4 w-4 mr-2" />{t('CostControl_import_open')}</DropdownMenuItem>}
            {af.lock && <DropdownMenuItem onClick={onLock}><Lock className="h-4 w-4 mr-2" />{t('CostControl_lock')}</DropdownMenuItem>}
            {af.unlock && <DropdownMenuItem onClick={onUnlock}><LockOpen className="h-4 w-4 mr-2" />{t('CostControl_unlock')}</DropdownMenuItem>}
            {af.openSettings && (canImport || hasLockItem) && <DropdownMenuSeparator />}
            {af.openSettings && <DropdownMenuItem onClick={onSettings}><Settings className="h-4 w-4 mr-2" />{t('CostControl_settings')}</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {af.addNode && (
        <Button size="sm" className="h-8" onClick={onAddLevelOne}>
          <FolderPlus className="h-4 w-4" />
          {t('CostControl_add_level_one')}
        </Button>
      )}
    </>
  )
}
export default BudgetActions
